/**
 * Act I — In the Beginning (Genesis 1–11).
 *
 * Four chapters: the garden, the fracture, the flood, the tower. Chapter ids
 * and verseIds are stable — progress is persisted against them, so they must
 * not be renamed.
 *
 * Scripture quoted inside scenes is the World English Bible (British Edition),
 * public domain, wrapped in *asterisks* so the renderer can set it in serif.
 */

export const ACT_BEGINNINGS = {
  id: 'beginnings',
  title: 'In the Beginning',
  summary: 'A good world, a fracture, and the first hint of a rescue.',
  span: 'Genesis 1–11',
  chapters: [
    // ── 1 ──────────────────────────────────────────────────────────────────
    {
      id: 'ch-creation',
      title: 'The Garden',
      where: 'Genesis 1–2',
      era: 'Before history',
      artCaption: "The garden as it was, with the river running out through it",
      scenes: [
        "The Bible does not open with an argument. It opens with a room being made ready. Light, then sky, then land and sea. Then, into each, something living. Six times the same verdict lands: *and God saw that it was good.* After people are made, it rises to very good. The order is not random. The first three days make empty spaces — day and night, sky and sea, dry ground. The next three fill them: sun and moon, birds and fish, animals and people. A house built, then furnished.",

        "On the sixth day the pattern breaks. God does not just speak humanity into being. He announces it first — let us make man in our image — and then makes them, male and female, in his likeness. Not an afterthought. The point. Chapter 2 tells the same story close up, and there God stoops down. A man is formed out of the dust and breathed into. The Hebrew keeps the joke: the man is *adam*, and the ground he is scooped from is *adamah*. Earth-creature from the earth.",

        "Genesis 2 drops the wide shot and walks you into one place. God plants a garden in the east, in Eden, and puts the man in it with work to do. Tend it and look after it. And one tree he is not to eat from. A river runs out of the garden and splits into four. Two of them are rivers you could go and find on a map: the Tigris and the Euphrates. Whatever else is happening here, the story has been pinned deliberately to real ground.",

        "Then comes the first thing in the Bible that God calls not good: the man is alone. The animals are brought to him to be named, and not one of them answers it. So the woman is made. The man's reply is the first human speech recorded in scripture, and it comes out as poetry. English Bibles call her a helper. That is a weak word in English and a strong one in Hebrew — it is used more often of God rescuing people than of anyone else. Not an assistant. A match.",

        "The week ends oddly. Every other day closes the same way: there was evening and there was morning. The seventh day has no such line. It is blessed, set apart, and left open. The Hebrew word behind sabbath means to stop, not to be tired. God is not recovering from the effort. He is settling into a finished world, the way a builder finally sits down in a house he means to live in. Israel will keep that day every week for the rest of the Old Testament.",

        "This is the world as it was meant to run. God present, people at home, everything in its place. Everything that follows is either the loss of this or the long road back. Two things are left hanging. There is a tree in the middle of the garden with a warning attached and no explanation given. And there is already something in the garden clever enough to talk. Christians read the six days differently — some as ordinary days, some as a frame around a far longer process. Genesis does not settle it.",
      ],
      verseId: 'gen-1-1',
      takeaway: 'The world began good, and people were made to bear God’s image in it.',
      questions: [
        {
          id: 'sq-creation-1',
          question: 'What verdict does Genesis 1 keep repeating over what God has made?',
          options: [
            'That it was finished',
            'That it was hidden',
            'That it was good',
            'That it was holy',
          ],
          answer: 2,
          explanation:
            'The refrain runs through the chapter and rises to “very good” once humanity is made. The Bible’s first verdict on the material world is approval, not suspicion.',
        },
        {
          id: 'sq-creation-2',
          question: 'What is the first thing in the Bible that God calls “not good”?',
          options: [
            'That no rain had yet fallen on the ground',
            'That the man was alone',
            'That the animals had no names',
            'That the garden had a boundary in it',
          ],
          answer: 1,
          explanation:
            'Genesis 2:18. Everything in chapter 1 is good; the first defect named anywhere in scripture is isolation, and it is named before anything has gone wrong.',
        },
        {
          id: 'sq-creation-3',
          question: 'The Hebrew word for the man in Genesis 2 is *adam*. What is *adamah*?',
          options: [
            'The breath of life',
            'The name of the garden',
            'The tree in the middle',
            'The ground he was formed from',
          ],
          answer: 3,
          explanation:
            'Genesis puns on the pair: the adam is lifted out of the adamah. The same pun returns in chapter 3, when he is told he will go back into it.',
        },
        {
          id: 'sq-creation-4',
          question: 'What makes the seventh day different from the other six?',
          options: [
            'It has no evening-and-morning ending, and is blessed and set apart',
            'It is the day the man and the woman are made',
            'It is the only day on which God speaks',
            'It is the day the garden is planted in Eden',
          ],
          answer: 0,
          explanation:
            'The other six days are closed off; the seventh is simply left open. The word behind sabbath means to stop rather than to recover, which is why the day is about presence rather than exhaustion.',
        },
      ],
    },

    // ── 2 ──────────────────────────────────────────────────────────────────
    {
      id: 'ch-fall',
      title: 'The Fracture',
      where: 'Genesis 3',
      era: 'Before history',
      artCaption: 'The tree in the middle, and the way out to the east',
      scenes: [
        "The garden had one rule. Everything here is yours; leave that one tree alone. Then a serpent turns up, and it does not argue. It asks a question. *Has God really said you shall not eat of any tree of the garden?* That is not even what God said, and the woman corrects it. But the question has already done its work. It has put an idea in the room: maybe the rule was never for your good. Maybe you are being kept from something.",
        "Then comes the offer. You will not die. Your eyes will be opened. You will be like God, knowing good and evil. It is not quite a lie, which is exactly why it works. She looks at the tree. It looks good. She eats, and hands some to her husband, who is standing right there and says nothing at all. Genesis never tells us what the fruit was. The apple is a much later invention. The story is not interested in the fruit — only in the reaching.",
        "Something changes straight away, and it is not the world. It is them. They notice they are naked and cover themselves up. They hear God walking in the garden, and they hide. God's first recorded question to a human being is not an accusation. It is *Where are you?* Then he asks what they have done. The man blames the woman — and, in the same breath, blames God for giving her to him. The woman blames the serpent. Nobody lies. Nobody owns it either.",
        "The consequences reach everything: the serpent, childbirth, and the ground itself, which will fight back every time it is worked. But two small things sit oddly inside all that. The man turns and gives his wife a name, Eve, which sounds like the Hebrew word for life — and calls her the mother of everyone living, at the very moment they are losing the life they had. And God makes them clothes out of animal skins. Something had to die so they could be covered.",
        "There is one more line, buried inside the curse on the serpent. One of the woman's descendants will crush its head, and be wounded doing it. Christians have read that sentence for two thousand years as the first hint of the rescue — the earliest promise of Jesus, long before anyone knew his name. Jewish readers have generally taken it more plainly, as why people and snakes have never got along. The sentence is short enough to carry both, and Genesis does not tell you which it meant.",
        "Then they are sent out. Out of the garden, out of the ease, out of walking with God in the cool of the day. Guards are posted on the east side with a flaming sword turning every way, blocking the path back to the tree of life. They do not drop dead that day, which readers have argued about ever since. What ends immediately is not their heartbeat. It is the garden — and being completely known by someone without being afraid of it.",
      ],
      verseId: 'gen-3-15',
      takeaway: 'Sin entered by distrust — and the rescue was promised in the same breath.',
      questions: [
        {
          id: 'sq-fall-1',
          question: 'How does the serpent open its approach to the woman?',
          options: [
            'By threatening her',
            'By questioning what God had said',
            'By offering her a kingdom',
            'By disguising itself as an angel',
          ],
          answer: 1,
          explanation:
            '“Has God really said…?” The temptation begins as a doubt about God’s goodness, not as a craving for fruit — the eating is the last step, not the first.',
        },
        {
          id: 'sq-fall-2',
          question: 'What does Genesis say the fruit of the tree was?',
          options: ['An apple', 'A fig', 'A pomegranate', 'It never says'],
          answer: 3,
          explanation:
            'The apple comes from a Latin pun — malum means both evil and apple — reinforced by centuries of painting. Genesis leaves the species entirely blank.',
        },
        {
          id: 'sq-fall-3',
          question: 'Why have Christians read Genesis 3:15 as the first hint of the gospel?',
          options: [
            'Because it names the woman as the mother of all living',
            'Because it promises the garden will one day be reopened',
            'Because it promises an offspring who will crush the serpent, and be wounded doing it',
            'Because it is the first time anyone in the Bible is forgiven',
          ],
          answer: 2,
          explanation:
            'The promise is spoken inside the judgement on the serpent rather than offered afterwards as consolation. Jewish readers have generally taken the same line as being about people and snakes.',
        },
      ],
    },

    // ── 3 ──────────────────────────────────────────────────────────────────
    {
      id: 'ch-flood',
      title: 'The Flood and the Bow',
      where: 'Genesis 6–9',
      era: 'Before history',
      artCaption: 'A box of gopher wood, and a bow hung in the clouds',
      scenes: [
        "Within a few generations the fracture has run through everything. Cain kills his brother in a field. Five generations later his descendant Lamech is boasting to his wives about killing a man for wounding him. Genesis 6 gives a bleak verdict: every intention of the human heart was only evil, all the time. Then comes a line that is easy to skim past. *It grieved him in his heart.* The first thing Genesis says about God once the world has gone wrong is not rage. It is grief.",

        "Noah is given measurements. Three hundred cubits by fifty by thirty — about 135 metres, longer than a football pitch. Genesis gives the thing no rudder, no sail and no front end. It is a box built to float, not to be steered. That is the point of it. The Hebrew word for it, *tevah*, turns up in only one other story in the whole Bible: the basket of reeds that keeps the baby Moses afloat on the Nile. Both are sealed containers carrying a future through water.",

        "The flood is judgement, and Genesis does not soften it. But the timing catches most readers out. The forty days everyone remembers are only the rain. Noah's family is aboard for more than a year. Genesis dates the boarding and the stepping off to the day of the month, and the gap runs past twelve months. Most of the story is not the storm. It is the waiting afterwards — sending out a raven, then a dove, three times over, until the dove does not come back.",

        "Read slowly, the middle of the story runs Genesis 1 backwards, and then forwards again. The waters above and below, split apart on the second day, come back together. Then *God remembered Noah.* A wind passes over the waters. Dry land appears. The birds go out. The survivors are told to be fruitful and multiply and fill the earth. Same elements, same order. The world is not scrapped and started again from nothing. It is carried through the water and handed back.",

        "Noah steps out onto drying ground and builds an altar. God makes a promise — never again — and hangs a bow in the clouds, pointed away from the earth. The Hebrew word is the ordinary one for a battle bow. It is the first covenant in the Bible, and God makes it not only with Noah but with every living creature. The reason is the strangest part. He will not curse the ground again because the human heart is bent from childhood. The diagnosis that brought the flood is now the grounds for mercy.",

        "Other peoples nearby told flood stories too, and the Babylonian ones we have are older than any copy of Genesis. In those, the gods send the water because people have grown too noisy, and the survivor is rewarded with immortality. Genesis keeps the boat and changes everything else. The reason is moral. The survivor stays mortal. The God who sends it grieves over it. Christians disagree over how far the water reached. Either way, Noah's next recorded act is to plant a vineyard and get drunk in his tent.",
      ],
      verseId: 'gen-9-13',
      takeaway: 'God judges sin and binds himself by promise not to abandon the world.',
      questions: [
        {
          id: 'sq-flood-1',
          question: 'What did God set in the clouds as the sign of his covenant after the flood?',
          options: ['A rainbow', 'A star', 'A dove', 'A pillar of cloud'],
          answer: 0,
          explanation:
            'Genesis 9:13. The Hebrew word is the ordinary word for a battle bow — hung up, aimed away from the earth. A weapon retired.',
        },
        {
          id: 'sq-flood-2',
          question: 'Which other story uses the same Hebrew word, *tevah*, that Genesis uses for the ark?',
          options: [
            'The ark of the covenant built at Sinai',
            'The boat the disciples are in during the storm',
            'The basket that carries the baby Moses on the Nile',
            'The great fish that swallows Jonah',
          ],
          answer: 2,
          explanation:
            'Two sealed containers, both carrying a rescue through water that should have drowned it. The ark of the covenant is an entirely different Hebrew word, despite the shared English.',
        },
        {
          id: 'sq-flood-3',
          question: 'How long were Noah and his family aboard?',
          options: ['Forty days', 'More than a year', 'Seven days', 'Three months'],
          answer: 1,
          explanation:
            'The forty days are the rainfall alone. Genesis dates the boarding and the disembarking to the day of the month, and the gap runs past twelve — most of the account is waiting, not weather.',
        },
        {
          id: 'sq-flood-4',
          question: 'What reason does God give for promising never to flood the earth again?',
          options: [
            'That Noah had proved himself righteous',
            'That enough sacrifices had been offered on the altar',
            'That the earth could not survive a second one',
            'That the human heart is bent from its youth',
          ],
          answer: 3,
          explanation:
            'Genesis 8:21. The same fact about human beings that provoked the judgement is now offered as the reason for restraint, which means the covenant never depended on people improving.',
        },
      ],
    },

    // ── 4 ──────────────────────────────────────────────────────────────────
    {
      id: 'ch-babel',
      title: 'The Tower',
      where: 'Genesis 11',
      era: 'Before history',
      artCaption: 'Brick, bitumen, and a tower on the plain of Shinar',
      scenes: [
        "They settle on a plain, work out how to make brick, and decide to build upward. Genesis 10 has just listed the nations — seventy of them, by the old count — already spread across the known world, exactly as they were told to be. Then chapter 11 rewinds and shows how that spreading actually happened, and what it cost. A people on the move stop in Shinar, which is southern Mesopotamia, and stay. The whole episode runs to nine verses, and it sets up everything after it.",

        "Genesis pauses to explain what they built with. *They had brick for stone, and they used tar for mortar.* It reads like a footnote. It is actually hard geography. The southern Iraqi floodplain has almost no building stone and very little timber. What it has is river mud, which bakes hard. And it has bitumen — natural tar that seeps up near the Euphrates and sets like glue. Anyone who had lived there would find the remark obvious. Anyone reading it in the stony hills of Canaan would find it foreign.",

        "The stated aim of the tower is not shelter or safety. It is *let’s make ourselves a name* — and, they add, so that we are not scattered over the face of the whole earth. It is the garden's temptation again, in civic form. Security and standing taken for ourselves rather than received. God had told people to fill the earth. Babel is a plan to stay put and stay famous. It is cooperative, skilled and impressive. Genesis shows an ambition that has quietly taken the place of an instruction.",

        "Towers like this were built all over Mesopotamia, and dozens have been dug up. A ziggurat is a temple tower, and it is not a skyscraper. It is a stepped platform of mud brick with a small shrine on top. Most likely the stairway up the side was for the god, not the worshippers — a way for him to come down to the city that built it. Genesis then lands the joke flat: the top of this tower reaches to the sky, and *the LORD came down to see* it.",

        "The judgement is almost gentle, and it lands on the project rather than the people. Their language is confused, and they scatter after all. Nobody dies. The name carries the verdict. In Babylonian the name meant gate of the god, which is the city's flattering account of itself. Genesis hears it instead as a Hebrew word meaning to confuse, and the grandest name in the ancient world becomes a word for babble. Babylon keeps coming back right through the Bible, and it always means the same thing: a civilisation certain it is the point.",

        "Genesis 11 ends with the human race spread across the map, split up, unable to understand each other. It is the last thing the Bible says about humanity in general. On the next page God stops working with humanity in general and starts with one man. The chapter closes on a family tree, and the flattest line in it matters most: Sarai was barren; she had no child. Then Terah takes the family out of Ur meaning to reach Canaan, gets as far as Haran, settles there instead, and dies there.",
      ],
      verseId: 'gen-11-4',
      takeaway: 'Humanity tried to build its way to greatness; God scattered the project.',
      questions: [
        {
          id: 'sq-babel-1',
          question: 'What reason do the builders of Babel give for building the tower?',
          options: [
            'To escape a second flood',
            'To worship the stars',
            'To make a name for themselves',
            'To store grain against a famine',
          ],
          answer: 2,
          explanation:
            'Genesis 11:4. Notice the contrast waiting one page later: at Babel they set out to make their own name, and in Genesis 12 God tells Abram, “I will make your name great.”',
        },
        {
          id: 'sq-babel-2',
          question: 'What were the builders working with, and why?',
          options: [
            'Mud brick and bitumen, because the Mesopotamian plain has almost no stone',
            'Cedar and iron, floated down the river from the north',
            'Cut limestone, quarried out of the hills of Shinar',
            'Sun-dried clay tablets, stacked in courses',
          ],
          answer: 0,
          explanation:
            'Genesis 11:3 mentions it in passing. Southern Mesopotamia is river silt — no quarries, little timber, and natural bitumen seeping up near the Euphrates to serve as mortar.',
        },
        {
          id: 'sq-babel-3',
          question: 'How does Genesis 11 leave the story?',
          options: [
            'With the tower finished, and the city renamed',
            'With the nations agreeing on a single language and law',
            'With Noah’s sons dividing the land between them',
            'With humanity scattered, and a family line running down to a barren couple',
          ],
          answer: 3,
          explanation:
            'The story of humanity in general stops at Babel. Genesis 12 starts again with one childless couple, and every promise the Bible afterwards makes to the world runs through them.',
        },
      ],
    },
  ],
};
